'use client';

import { useEffect, useRef, useState } from 'react';
import { BOLD_CONFIG } from '@/lib/payment-config';
import type { GuestData } from './usePaymentForm';
import { recordCheckoutObservation } from '@/lib/payments/checkout-trace-client';
import { loadBoldCheckout } from '@/lib/payments/bold-checkout-client';
import { splitE164 } from '@/lib/phone/countries';

/**
 * Flujo Bold compartido (apartamentos y tours): generación del orderId +
 * integrity hash server-side y montaje del botón con redirección de Bold.
 *
 * El secreto firma en /api/bold/generate-hash (server); aquí solo viaja la
 * llave pública. El hook NO decide cuándo se genera (cada modal valida lo
 * suyo antes); expone `generateOrder` y el efecto de carga única del SDK.
 */

// FIX 2026-05-01: regex /^\+(\d{1,3})/ era greedy, capturaba 3 dígitos
// siempre (+573 en vez de +57 para Colombia). Bold rechazaba con BTN-001
// porque +573 no es código país válido.
// Solución: matchear contra la lista exacta de indicativos. Desde el 14-oct-2026
// es la tabla completa del selector (splitE164); antes un +31 caía en el +57.

// FIX 2026-07-01 (BTN-001 reproducido): Bold exige `data-description` de 2 a 100
// caracteres (doc oficial). El anticipo 25% de tours generaba ~121-127 chars →
// Bold redirigía a checkout.bold.co/error/generic-error con BTN-001 y NINGÚN
// pago con anticipo podía completarse (caso Aníbal + 2º cliente, 2026-07-01).
export const BOLD_DESCRIPTION_MIN = 2;
export const BOLD_DESCRIPTION_MAX = 100;

export function clampBoldDescription(description: string): string {
  const d = description.trim();
  if (d.length < BOLD_DESCRIPTION_MIN) return 'Reserva Pa Cartagena';
  if (d.length <= BOLD_DESCRIPTION_MAX) return d;
  return `${d.slice(0, BOLD_DESCRIPTION_MAX - 1).trimEnd()}…`;
}

export function splitDialCode(rawPhone: string): { dialCode: string; phoneNumber: string } {
  const phoneNumber = rawPhone.trim();
  const split = splitE164(phoneNumber);
  if (split) return { dialCode: `+${split.dial}`, phoneNumber: split.national };
  if (phoneNumber.startsWith('57')) return { dialCode: '+57', phoneNumber: phoneNumber.substring(2) };
  return { dialCode: '+57', phoneNumber };
}

export interface UseBoldPaymentParams {
  locale?: 'es' | 'en';
  /** id del div donde Bold monta el botón (distinto por modal) */
  containerId: string;
  /** monto a cobrar (depósito) */
  amount: number;
  /** descripción del cobro que ve el cliente en Bold */
  description: string;
  /** datos del titular ya validados */
  guestData: GuestData;
  /** origen del redirect si window no está disponible (SSR teórico) */
  fallbackRedirectOrigin: string;
  /** prefijo del orderId: 'RES-' en apartamentos, 'TOUR-' en tours */
  orderPrefix?: string;
  /**
   * Contexto que el servidor revalida ANTES de firmar un cobro de tour. No es
   * opcional en la práctica para referencias `TOUR-*`: el endpoint rechaza la
   * firma si falta o si la fecha está dentro del cutoff persistido del tour.
   */
  tourPaymentContext?: {
    encuentro?: { revision: number; locationId: string; lodging?: string };
    tourId: string;
    availabilityId: string | null;
    tourDate: string;
    tourTime: string | null;
    numAdults: number;
    numChildren: number;
    participants?: Array<{ tierKey: string; quantity: number }>;
    chargeMode: 'full' | 'deposit';
    expectedTotalCop?: number;
    expectedChargeCop?: number;
    attribution?: { gclid?: string; gbraid?: string; wbraid?: string };
    _hp?: string;
    turnstileToken?: string | null;
  };
  onError: (message: string) => void;
  /**
   * Hook de observabilidad justo antes de montar el script de Bold (tours lo
   * usa para trackear la FORMA del payload — diagnóstico BTN-001, sin PII).
   */
  onBeforeMount?: (info: {
    orderId: string;
    customerData: { email: string; fullName: string; phone: string; dialCode: string };
    description: string;
  }) => void;
  /**
   * Observabilidad del CICLO DE VIDA del script embebido de Bold (caso Aníbal
   * 2026-07-01: 2 bold_checkout_attempt sin transacción en Bold y sin rastro —
   * si el CDN falla o un bloqueador corta el script, el botón nunca aparece y
   * el cliente re-clickea/recarga sin que quede evidencia). `phase` es
   * 'loaded' (script cargó, el botón debería renderizar) o 'script_error'.
   */
  onScriptLifecycle?: (phase: 'loaded' | 'script_error', info: { orderId: string }) => void;
}

export function useBoldPayment({
  locale = 'es',
  containerId,
  amount,
  description,
  guestData,
  fallbackRedirectOrigin,
  orderPrefix = 'RES-',
  tourPaymentContext,
  onError,
  onBeforeMount,
  onScriptLifecycle,
}: UseBoldPaymentParams) {
  const [integrityHash, setIntegrityHash] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [providerAmount, setProviderAmount] = useState<number | null>(null);
  const [generatingHash, setGeneratingHash] = useState(false);
  const clientRequestIdRef = useRef<string | null>(null);

  // Genera orderId + hash de integridad (server-side). Devuelve el orderId o
  // null si falló (el error ya se reportó vía onError... no: el caller maneja
  // el catch — esta función LANZA para que cada modal arme su mensaje).
  const generateOrder = async (): Promise<string> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), BOLD_CONFIG.timeout);

    try {
      let newOrderId: string;
      let canonicalAmount = amount;

      if (tourPaymentContext) {
        // La orden durable nace ANTES de abrir Bold. El UUID del cliente solo
        // sirve para idempotencia de retry; la referencia y el monto los decide
        // y persiste el servidor.
        if (!clientRequestIdRef.current) {
          if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            clientRequestIdRef.current = crypto.randomUUID();
          } else {
            throw new Error('Tu navegador no permite preparar un identificador seguro de pago.');
          }
        }
        const orderResponse = await fetch('/api/tours/checkout-orders', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientRequestId: clientRequestIdRef.current,
            ...tourPaymentContext,
            guest: {
              language: locale,
              name: guestData.name,
              phone: guestData.phone,
              email: guestData.email || null,
            },
          }),
          signal: controller.signal,
        });
        const orderData = await orderResponse.json();
        if (!orderResponse.ok || !orderData.success) {
          throw new Error(orderData.error || 'No se pudo crear la orden de pago');
        }
        if (
          typeof orderData.orderId !== 'string' ||
          !Number.isInteger(orderData.amount) ||
          orderData.amount <= 0
        ) {
          throw new Error('El servidor devolvió una orden de pago incompleta.');
        }
        newOrderId = orderData.orderId;
        canonicalAmount = orderData.amount;
      } else if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        newOrderId = `${orderPrefix}${crypto.randomUUID()}`;
      } else {
        newOrderId = `${orderPrefix}${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      }

      const response = await fetch('/api/bold/generate-hash', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: newOrderId,
          amount: canonicalAmount,
          currency: 'COP',
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Error generando hash de pago');
      }
      if (!Number.isInteger(data.amount) || data.amount !== canonicalAmount || data.currency !== 'COP') {
        throw new Error('La firma no coincide con el monto persistido de la orden.');
      }
      setOrderId(data.orderId);
      setProviderAmount(data.amount);
      setIntegrityHash(data.hash);
      return data.orderId;
    } catch (fetchError: unknown) {
      clearTimeout(timeoutId);
      if ((fetchError as { name?: string })?.name === 'AbortError') {
        throw new Error('Tiempo de espera agotado. Verifica tu conexión e intenta de nuevo.');
      }
      throw fetchError;
    }
  };

  const resetBold = () => {
    setIntegrityHash(null);
    setOrderId(null);
    setProviderAmount(null);
    setGeneratingHash(false);
    clientRequestIdRef.current = null;
  };

  // Monta el botón de redirección de Bold cuando hay hash (mismo efecto que tenían
  // ambos modales, parametrizado por containerId/description/amount).
  useEffect(() => {
    if (!integrityHash || !orderId || !providerAmount) return;
    if (!BOLD_CONFIG.apiKey) {
      onError('Error de configuración: API Key de Bold no disponible');
      return;
    }
    const container = document.getElementById(containerId);
    if (!container) {
      recordCheckoutObservation(orderId, 'button_missing');
      onError('Error interno: contenedor del botón no encontrado');
      return;
    }
    const { dialCode, phoneNumber } = splitDialCode(guestData.phone);
    const customerData = {
      email: guestData.email.trim(),
      fullName: guestData.name.trim(),
      phone: phoneNumber,
      dialCode,
    };
    const absoluteRedirectUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/payment/confirm`
        : `${fallbackRedirectOrigin}/payment/confirm`;

    // Bold rechaza descripciones >100 chars con BTN-001 → SIEMPRE clampear acá,
    // sin depender de que cada modal componga un texto corto.
    const safeDescription = clampBoldDescription(description);

    onBeforeMount?.({ orderId, customerData, description: safeDescription });
    let disposed = false;
    let clicked = false;
    let left = false;
    const button = document.createElement('button');
    button.type = 'button';
    button.disabled = true;
    button.className = 'w-full min-h-12 rounded-xl bg-[#121e6c] px-6 py-3 font-semibold text-white shadow-sm hover:bg-[#1b2d93] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60';
    button.textContent = locale === 'en' ? 'Loading secure payment…' : 'Cargando pago seguro…';
    button.dataset.boldCheckoutButton = 'true';
    container.appendChild(button);
    const onPageHide = () => {
      if (clicked && !left) { left = true; recordCheckoutObservation(orderId, 'checkout_left'); }
    };
    window.addEventListener('pagehide', onPageHide);
    // Una sola carga del SDK evita reejecutar sus registros de customElements.
    // La redirección evita insertar un iframe fuera del Dialog modal de Radix.
    void loadBoldCheckout().then(BoldCheckout => {
      if (disposed) return;
      const checkout = new BoldCheckout({
        apiKey: BOLD_CONFIG.apiKey, amount: providerAmount.toString(), currency: 'COP',
        orderId, integritySignature: integrityHash, description: safeDescription,
        redirectionUrl: absoluteRedirectUrl, customerData: JSON.stringify(customerData),
      });
      button.disabled = false;
      button.textContent = locale === 'en' ? 'Pay securely with Bold' : 'Pagar de forma segura con Bold';
      recordCheckoutObservation(orderId, 'button_mounted');
      onScriptLifecycle?.('loaded', { orderId });
      button.onclick = () => {
        clicked = true;
        recordCheckoutObservation(orderId, 'button_clicked');
        try { checkout.open(); }
        catch {
          recordCheckoutObservation(orderId, 'checkout_open_error');
          onError(locale === 'en' ? 'The payment could not open. Try again or arrange payment on WhatsApp.' : 'No se pudo abrir el pago. Reintenta o coordina el pago por WhatsApp.');
        }
      };
    }).catch(() => {
      if (disposed) return;
      recordCheckoutObservation(orderId, 'script_load_error');
      onScriptLifecycle?.('script_error', { orderId });
      button.textContent = locale === 'en' ? 'Payment unavailable' : 'Pago no disponible';
      onError(locale === 'en' ? 'The payment button could not load. Check your connection or arrange payment on WhatsApp.' : 'No se pudo cargar el botón de pago. Revisa tu conexión o coordina el pago por WhatsApp.');
    });
    return () => {
      disposed = true;
      window.removeEventListener('pagehide', onPageHide);
      button.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    integrityHash,
    orderId,
    providerAmount,
    description,
    containerId,
    locale,
    guestData.email,
    guestData.name,
    guestData.phone,
  ]);

  return {
    integrityHash,
    orderId,
    providerAmount,
    generatingHash,
    setGeneratingHash,
    setIntegrityHash,
    setOrderId,
    generateOrder,
    resetBold,
  };
}

/** Mensaje de error amigable compartido por ambos modales. */
export function friendlyPaymentError(error: unknown, fallback: string, locale: 'es' | 'en' = 'es'): string {
  const message = (error as { message?: string })?.message;
  if (message) {
    if (locale === 'en') {
      if (/^The payment (?:button )?could not (?:open|load)\./.test(message)) return message;
      if (/fetch|NetworkError|conexión|espera agotado/i.test(message)) return 'Connection error. Check your internet connection and try again.';
      if (/cupo|disponib|capacidad/i.test(message)) return 'There is not enough availability for this selection. Choose another date or contact us on WhatsApp.';
      if (/anticipación|fecha|salida/i.test(message)) return 'This date is no longer bookable online. Choose a later date or check availability on WhatsApp.';
      if (/Turnstile|verificación|captcha/i.test(message)) return 'Please complete the security check and try again.';
      if (/personas|participantes|tarifa/i.test(message)) return 'Check your group size and participant ages before continuing.';
      if (/correo|email/i.test(message)) return 'Check your email address and try again.';
      if (/teléfono|whatsapp/i.test(message)) return 'Check your WhatsApp number, including its country code.';
      if (/precio|monto|total/i.test(message)) return 'The price could not be confirmed. Refresh the page and check your selection.';
      return fallback;
    }
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
      return 'Error de conexión. Verifica tu internet e intenta de nuevo.';
    }
    return message;
  }
  return fallback;
}
